import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { http, gradeLabel } from "@/lib/api";
import { RecordShell, LetterBadge, GRADING_SCALE } from "@/components/records/RecordShell";

const HighSchool = ({ hs }) => (
  <section className="mt-10" data-testid="transcript-high-school">
    <div className="flex flex-wrap justify-between items-end gap-3 border-b-2 border-[#2C4A3E] pb-2">
      <p className="font-display text-xl text-[#2C4A3E]">High School Record (Grades 9-12)</p>
      <p className="text-sm text-stone-700">GPA <b data-testid="transcript-gpa">{hs.gpa ?? "—"}</b> / 4.0 · Credits <b data-testid="transcript-credits">{hs.credits_earned}</b> of {hs.credits_required}{hs.credits_in_progress > 0 && ` (+${hs.credits_in_progress} in progress)`}</p>
    </div>
    <table className="w-full text-sm mt-2">
      <thead><tr className="text-left text-xs text-stone-500"><th className="py-2">Year</th><th>Course</th><th>Gr</th><th>Avg</th><th>Grade</th><th>Credit</th><th>Status</th></tr></thead>
      <tbody>
        {hs.courses.map((c) => (
          <tr key={`${c.school_year}-${c.id}`} className="border-t border-[#E6DEC3]/60" data-testid={`transcript-course-${c.id}`}>
            <td className="py-2 text-stone-500 whitespace-nowrap">{c.school_year}</td><td className="text-stone-800">{c.title}</td><td>{c.grade}</td>
            <td>{c.average ?? "—"}</td><td><LetterBadge letter={c.letter} /></td><td>{c.status === "In progress" ? `(${c.credit})` : c.credit_earned.toFixed(1)}</td>
            <td className={`text-xs ${c.status === "Earned" ? "text-[#2C4A3E]" : c.status === "Not earned" ? "text-red-700" : "text-stone-500"}`}>{c.status}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="text-xs uppercase tracking-widest text-stone-500 font-semibold mt-6">Graduation progress (23 credits)</p>
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-3">
      {hs.requirements.map((r) => (
        <div key={r.area} className="text-sm" data-testid={`grad-req-${r.area.replace(/\W+/g, "-").toLowerCase()}`}>
          <div className="flex justify-between"><span className="text-stone-700">{r.area}</span><span className="text-stone-500">{r.earned} / {r.required}</span></div>
          <div className="h-1.5 rounded-full bg-[#F1EADB] mt-1"><div className="h-full rounded-full bg-[#6B9080]" style={{ width: `${Math.min(100, (r.earned / r.required) * 100)}%` }} /></div>
        </div>
      ))}
    </div>
    <p className="text-[10px] text-stone-400 mt-3">GPA: unweighted 4.0 scale (A=4, B=3, C=2, F=0), credit-weighted. A 1.0 credit is earned at the end of the school year with an average of 70 or higher.</p>
  </section>
);

export default function Transcript() {
  const { studentId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { http.get(`/students/${studentId}/transcript`).then((r) => setD(r.data)).catch(() => setErr("You don't have access to this transcript.")); }, [studentId]);
  if (err) return <p className="text-center py-20 text-stone-500" data-testid="record-error">{err}</p>;
  if (!d) return <p className="text-center py-20 text-stone-500">Loading…</p>;

  return (
    <RecordShell title="Academic Transcript" back testId="transcript-page">
      <div className="grid sm:grid-cols-4 gap-4 mt-6 text-sm">
        <div className="sm:col-span-2">
          <h1 className="font-display text-3xl text-[#2C4A3E]" data-testid="transcript-student-name">{d.student.name}</h1>
          <p className="text-stone-500">{d.student.email} · Current: {gradeLabel(d.student.grade)}</p>
        </div>
        <div><p className="text-xs text-stone-500 uppercase tracking-widest">Cumulative</p>
          <p className="font-display text-2xl text-[#2C4A3E]" data-testid="transcript-cumulative">{d.cumulative_average ?? "—"} <LetterBadge letter={d.cumulative_letter} /></p></div>
        <div><p className="text-xs text-stone-500 uppercase tracking-widest">Totals</p>
          <p className="text-stone-700">{d.total_lessons} lessons · {d.total_days_present} days present</p></div>
      </div>

      {d.high_school && <HighSchool hs={d.high_school} />}
      {d.years.map((y) => (
        <section key={`${y.school_year}-${y.grade}`} className="mt-10" data-testid={`transcript-year-${y.school_year}-${y.grade}`}>
          <div className="flex justify-between items-end border-b-2 border-[#2C4A3E] pb-2">
            <p className="font-display text-xl text-[#2C4A3E]">{gradeLabel(y.grade)} · {y.school_year}</p>
            <p className="text-sm text-stone-600">Avg {y.average ?? "—"} · {y.days_present} days present</p>
          </div>
          <table className="w-full text-sm mt-2">
            <thead><tr className="text-left text-xs text-stone-500"><th className="py-2">Subject</th><th>Lessons</th><th>Average</th><th>Grade</th></tr></thead>
            <tbody>
              {y.subjects.map((s) => (
                <tr key={s.subject_id} className="border-t border-[#E6DEC3]/60">
                  <td className="py-2.5 text-stone-800">{s.subject_name}</td><td>{s.lessons}</td><td>{s.average ?? "—"}</td><td><LetterBadge letter={s.letter} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
      {!d.years.length && <p className="text-center text-stone-500 py-12" data-testid="transcript-empty">No completed coursework yet.</p>}
      <p className="text-xs text-stone-400 mt-10">{GRADING_SCALE} Present = at least {d.goal_minutes} active minutes in a day.</p>
      <p className="text-[10px] text-stone-400 mt-1">Unofficial transcript · Generated {new Date(d.generated_at).toLocaleString()}</p>
    </RecordShell>
  );
}
